import { useState } from 'react';
import { Banknote, CreditCard } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PageHeader } from '@/components/shared/page-header';
import { RevelReconciliationPanel } from './revel-reconciliation-panel';
import { SupernovaReconciliationPanel } from './supernova-reconciliation-panel';
import { CashReconciliationPanel } from './reconciliation-cash-panel';
import { usePermissions } from '@/hooks/use-permissions';
import type { PermissionKey } from '@supernova/shared';

type ReconciliationMode = 'revel' | 'supernova';
type ReconciliationTab = 'card' | 'cash';

// SuperNova is the default and shown first; the legacy "Revel" source is now
// surfaced as "Others" (internal id unchanged — UI relabel only).
const MODES: { id: ReconciliationMode; label: string }[] = [
  { id: 'supernova', label: 'SuperNova' },
  { id: 'revel', label: 'Others' },
];

const TABS: { id: ReconciliationTab; label: string; icon: React.ElementType; view: PermissionKey }[] = [
  { id: 'card', label: 'Card', icon: CreditCard, view: 'reconciliation.card.view' },
  { id: 'cash', label: 'Cash', icon: Banknote, view: 'reconciliation.cash.view' },
];

export function ReconciliationPage() {
  const [selectedTab, setTab] = useState<ReconciliationTab>('card');
  const [mode, setMode] = useState<ReconciliationMode>('supernova');
  // Card and Cash each open with their own View; show only those held.
  const permissions = usePermissions();
  const tabs = TABS.filter((t) => permissions.has(t.view));
  const tab: ReconciliationTab | undefined = tabs.some((t) => t.id === selectedTab) ? selectedTab : tabs[0]?.id;

  const description =
    tab === 'cash'
      ? 'Reconcile cash payments and drawer counts.'
      : mode === 'revel'
        ? 'Match card sales against processor funding settlements and spot drops, unexpected charges, or duplicate transactions.'
        : 'Match SuperNova order items against recorded payments to catch unpaid orders, missing item breakdowns, and duplicate charges.';

  return (
    <div className="space-y-4">
      <PageHeader title="Reconciliation" description={description}>
        {/* Source toggle only applies to the Card view. */}
        {tab === 'card' && (
          <div className="inline-flex items-center rounded-md border border-input bg-white p-0.5">
            {MODES.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => setMode(m.id)}
                className={cn(
                  'rounded px-3 py-1 text-sm font-medium transition-colors',
                  mode === m.id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {m.label}
              </button>
            ))}
          </div>
        )}
      </PageHeader>

      {/* Top-level Card / Cash tabs — standard report-page tab style. */}
      <div className="scrollbar-hide overflow-x-auto border-b border-border">
        <div className="flex w-full min-w-0" role="tablist" aria-label="Reconciliation payment type">
          {tabs.map((t) => {
            const isSelected = tab === t.id;
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={cn(
                  'relative flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap px-4 py-2.5 text-center text-xs font-medium transition-colors sm:text-sm',
                  isSelected ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
                )}
                aria-selected={isSelected}
                role="tab"
              >
                <Icon className="h-4 w-4" />
                {t.label}
                {isSelected && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />}
              </button>
            );
          })}
        </div>
      </div>

      {tab === 'card' && (mode === 'revel' ? <RevelReconciliationPanel /> : <SupernovaReconciliationPanel />)}
      {tab === 'cash' && <CashReconciliationPanel />}
    </div>
  );
}
